import React, { useEffect, useState, useRef } from 'react';
import {
  Paper,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  CardActions,
  Button,
  Chip,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  InputAdornment,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  Email as EmailIcon,
  Description as DescriptionIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Save as SaveIcon,
} from '@mui/icons-material';
import { vorlagenApi, mandantenApi, gruppenApi } from '../services/api';
import type { Vorlage, VorlageContent, Mandant, Gruppe } from '../types/api';
import SyncfusionEditor, { SyncfusionEditorHandle } from '../components/SyncfusionEditor';
import { getApiBaseUrl } from '../utils/apiUrl';

const Vorlagen: React.FC = () => {
  const [vorlagen, setVorlagen] = useState<Vorlage[]>([]);
  const [mandanten, setMandanten] = useState<Mandant[]>([]);
  const [gruppen, setGruppen] = useState<Gruppe[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedVorlage, setSelectedVorlage] = useState<VorlageContent | null>(
    null
  );
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  // Für neue Vorlage
  const [newVorlage, setNewVorlage] = useState({
    vorlageName: '',
    vorlageTyp: 'Brief',
    dateiPfad: '',
    dateiFormat: '.md',
    beschreibung: '',
    kategorie: '',
    mandantID: undefined as number | undefined,
    gruppeID: undefined as number | undefined,
  });

  // Für Bearbeitung
  const [editVorlage, setEditVorlage] = useState({
    vorlageID: 0,
    vorlageName: '',
    vorlageTyp: '',
    dateiPfad: '',
    dateiFormat: '.md',
    beschreibung: '',
    kategorie: '',
    mandantID: undefined as number | undefined,
    gruppeID: undefined as number | undefined,
  });

  // Für Datei-Upload
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  // Für Inhalt beim Bearbeiten
  const [editContent, setEditContent] = useState<string>('');

  // Für Syncfusion Editor
  const editorRef = useRef<SyncfusionEditorHandle>(null);
  const [isDocxVorlage, setIsDocxVorlage] = useState(false);
  const [, setEditorLoading] = useState(false);
  const [contentChanged, setContentChanged] = useState(false);

  useEffect(() => {
    loadVorlagen();
  }, []);

  const loadVorlagen = async () => {
    try {
      setLoading(true);
      setError(null);

      // Lade Vorlagen (erforderlich)
      const response = await vorlagenApi.getAll();
      setVorlagen(response.data);

      // Lade Mandanten und Gruppen (optional, nur für Admins)
      try {
        const mandantenResponse = await mandantenApi.getAll();
        setMandanten(mandantenResponse.data);
      } catch (err: any) {
        console.log('Mandanten konnten nicht geladen werden (benötigt Admin-Rechte)');
        setMandanten([]);
      }

      try {
        const gruppenResponse = await gruppenApi.getAll();
        setGruppen(gruppenResponse.data);
      } catch (err: any) {
        console.log('Gruppen konnten nicht geladen werden');
        setGruppen([]);
      }
    } catch (err: any) {
      console.error('Vorlagen-Fehler:', err);
      setError(err.response?.data?.message || 'Fehler beim Laden der Vorlagen');
    } finally {
      setLoading(false);
    }
  };

  const handleViewVorlage = async (vorlageId: number) => {
    try {
      const response = await vorlagenApi.getContent(vorlageId);
      setSelectedVorlage(response.data);
      setViewDialogOpen(true);
    } catch (err: any) {
      alert('Fehler beim Laden des Vorlage-Inhalts');
    }
  };

  const handleDelete = async (vorlageId: number) => {
    if (!window.confirm('Vorlage wirklich löschen?')) return;

    try {
      await vorlagenApi.delete(vorlageId);
      setVorlagen(vorlagen.filter((v) => v.vorlageID !== vorlageId));
    } catch (err: any) {
      alert('Fehler beim Löschen der Vorlage');
    }
  };

  const handleCreateVorlage = async () => {
    if (!newVorlage.vorlageName.trim()) {
      alert('Bitte geben Sie einen Namen ein');
      return;
    }

    try {
      setSaving(true);
      let response;

      // Wenn eine Datei ausgewählt wurde, verwende Upload
      if (selectedFile) {
        response = await vorlagenApi.upload(
          selectedFile,
          newVorlage.vorlageName,
          newVorlage.vorlageTyp,
          newVorlage.beschreibung || undefined,
          newVorlage.kategorie || undefined,
          newVorlage.mandantID,
          newVorlage.gruppeID
        );
      } else {
        // Ansonsten normales Create ohne Datei
        response = await vorlagenApi.create({
          vorlageName: newVorlage.vorlageName,
          vorlageTyp: newVorlage.vorlageTyp,
          dateiPfad: newVorlage.dateiPfad || undefined,
          dateiFormat: newVorlage.dateiFormat,
          beschreibung: newVorlage.beschreibung || undefined,
          kategorie: newVorlage.kategorie || undefined,
          mandantID: newVorlage.mandantID,
          gruppeID: newVorlage.gruppeID,
        });
      }

      setVorlagen([...vorlagen, response.data]);
      setCreateDialogOpen(false);
      setNewVorlage({
        vorlageName: '',
        vorlageTyp: 'Brief',
        dateiPfad: '',
        dateiFormat: '.md',
        beschreibung: '',
        kategorie: '',
        mandantID: undefined,
        gruppeID: undefined,
      });
      setSelectedFile(null);
    } catch (err: any) {
      alert('Fehler beim Erstellen der Vorlage: ' + (err.response?.data?.message || err.message));
    } finally {
      setSaving(false);
    }
  };

  const handleEditClick = async (vorlage: Vorlage) => {
    // Prüfe ob es eine DOCX-Vorlage ist
    const isDocx = vorlage.dateiPfad?.toLowerCase().endsWith('.docx') || false;
    setIsDocxVorlage(isDocx);
    setContentChanged(false);

    setEditVorlage({
      vorlageID: vorlage.vorlageID,
      vorlageName: vorlage.vorlageName,
      vorlageTyp: vorlage.vorlageTyp,
      dateiPfad: vorlage.dateiPfad,
      dateiFormat: isDocx ? '.docx' : '.md',
      beschreibung: vorlage.beschreibung || '',
      kategorie: '',
      mandantID: vorlage.mandantID,
      gruppeID: vorlage.gruppeID,
    });

    // Lade den Inhalt der Vorlage (nur für nicht-DOCX)
    if (!isDocx) {
      try {
        const response = await vorlagenApi.getContent(vorlage.vorlageID);
        setEditContent(response.data.content || '');
      } catch (err: any) {
        console.error('Fehler beim Laden des Inhalts:', err);
        setEditContent('Fehler beim Laden des Inhalts');
      }
    } else {
      setEditContent('');
      setEditorLoading(true);
    }

    setEditDialogOpen(true);
  };

  // Vorlage speichern (für DOCX mit Syncfusion Editor)
  const handleSaveVorlageContent = async () => {
    if (!isDocxVorlage || !editorRef.current) return;

    try {
      setSaving(true);
      const blob = await editorRef.current.getDocumentAsBlob();
      if (!blob) {
        alert('Fehler: Konnte Dokument nicht exportieren');
        return;
      }

      const formData = new FormData();
      formData.append('file', blob, 'vorlage.docx');

      const apiUrl = getApiBaseUrl();
      const response = await fetch(`${apiUrl}/api/syncfusion/SaveVorlage/${editVorlage.vorlageID}`, {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Fehler beim Speichern');
      }

      setContentChanged(false);
      alert('Vorlage erfolgreich gespeichert!');
    } catch (err: any) {
      console.error('Fehler beim Speichern der Vorlage:', err);
      alert('Fehler beim Speichern: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateVorlage = async () => {
    if (!editVorlage.vorlageName.trim()) {
      alert('Bitte geben Sie einen Namen ein');
      return;
    }

    try {
      setSaving(true);
      const response = await vorlagenApi.update(editVorlage.vorlageID, {
        vorlageName: editVorlage.vorlageName,
        vorlageTyp: editVorlage.vorlageTyp,
        dateiPfad: editVorlage.dateiPfad || undefined,
        dateiFormat: editVorlage.dateiFormat,
        beschreibung: editVorlage.beschreibung || undefined,
        kategorie: editVorlage.kategorie || undefined,
        mandantID: editVorlage.mandantID,
        gruppeID: editVorlage.gruppeID,
      });

      setVorlagen(
        vorlagen.map((v) =>
          v.vorlageID === editVorlage.vorlageID ? response.data : v
        )
      );
      setEditDialogOpen(false);
    } catch (err: any) {
      alert('Fehler beim Aktualisieren der Vorlage: ' + (err.response?.data?.message || err.message));
    } finally {
      setSaving(false);
    }
  };

  const filteredVorlagen = vorlagen.filter(
    (vorlage) =>
      vorlage.vorlageName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (vorlage.beschreibung || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  const briefVorlagen = filteredVorlagen.filter((v) => v.vorlageTyp === 'Brief');
  const emailVorlagen = filteredVorlagen.filter((v) => v.vorlageTyp === 'Email');

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      </Box>
    );
  }

  const VorlageCard = ({ vorlage }: { vorlage: Vorlage }) => (
    <Card>
      <CardContent>
        <Box display="flex" alignItems="center" gap={1} mb={1}>
          {vorlage.vorlageTyp === 'Brief' ? (
            <DescriptionIcon color="primary" />
          ) : (
            <EmailIcon color="primary" />
          )}
          <Typography variant="h6">{vorlage.vorlageName}</Typography>
        </Box>
        {vorlage.beschreibung && (
          <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
            {vorlage.beschreibung}
          </Typography>
        )}
        <Box display="flex" gap={1} flexWrap="wrap">
          <Chip label={vorlage.vorlageTyp} size="small" color="primary" />
          <Chip
            label={new Date(vorlage.erstelltAm).toLocaleDateString('de-DE')}
            size="small"
            variant="outlined"
          />
          {vorlage.mandantName && (
            <Chip
              label={`Mandant: ${vorlage.mandantName}`}
              size="small"
              color="secondary"
              variant="outlined"
            />
          )}
          {vorlage.gruppeName && (
            <Chip
              label={`Gruppe: ${vorlage.gruppeName}`}
              size="small"
              color="info"
              variant="outlined"
            />
          )}
        </Box>
      </CardContent>
      <CardActions>
        <Tooltip title="Anzeigen">
          <IconButton
            size="small"
            onClick={() => handleViewVorlage(vorlage.vorlageID)}
          >
            <ViewIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Bearbeiten">
          <IconButton
            size="small"
            color="primary"
            onClick={() => handleEditClick(vorlage)}
          >
            <EditIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Löschen">
          <IconButton
            size="small"
            color="error"
            onClick={() => handleDelete(vorlage.vorlageID)}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </CardActions>
    </Card>
  );

  return (
    <Box>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
      >
        <Typography variant="h4">Vorlagen</Typography>
        <Box display="flex" gap={2}>
          <TextField
            size="small"
            placeholder="Suchen..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setCreateDialogOpen(true)}
          >
            Neue Vorlage
          </Button>
          <Tooltip title="Aktualisieren">
            <IconButton onClick={loadVorlagen} color="primary">
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Brief-Vorlagen */}
      <Box mb={4}>
        <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <DescriptionIcon /> Brief-Vorlagen ({briefVorlagen.length})
        </Typography>
        {briefVorlagen.length === 0 ? (
          <Paper sx={{ p: 3, textAlign: 'center' }}>
            <Typography color="textSecondary">
              Keine Brief-Vorlagen vorhanden
            </Typography>
          </Paper>
        ) : (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                sm: '1fr 1fr',
                md: '1fr 1fr 1fr',
              },
              gap: 2,
            }}
          >
            {briefVorlagen.map((vorlage) => (
              <VorlageCard key={vorlage.vorlageID} vorlage={vorlage} />
            ))}
          </Box>
        )}
      </Box>

      {/* E-Mail-Vorlagen */}
      <Box>
        <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <EmailIcon /> E-Mail-Vorlagen ({emailVorlagen.length})
        </Typography>
        {emailVorlagen.length === 0 ? (
          <Paper sx={{ p: 3, textAlign: 'center' }}>
            <Typography color="textSecondary">
              Keine E-Mail-Vorlagen vorhanden
            </Typography>
          </Paper>
        ) : (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                sm: '1fr 1fr',
                md: '1fr 1fr 1fr',
              },
              gap: 2,
            }}
          >
            {emailVorlagen.map((vorlage) => (
              <VorlageCard key={vorlage.vorlageID} vorlage={vorlage} />
            ))}
          </Box>
        )}
      </Box>

      {/* Dialog zum Anzeigen der Vorlage */}
      <Dialog
        open={viewDialogOpen}
        onClose={() => setViewDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        {selectedVorlage && (
          <>
            <DialogTitle>{selectedVorlage.vorlageName}</DialogTitle>
            <DialogContent dividers>
              <Box mb={2}>
                <Typography variant="subtitle2" color="textSecondary">
                  Typ:
                </Typography>
                <Chip label={selectedVorlage.vorlageTyp} size="small" />
              </Box>
              <Box mb={2}>
                <Typography variant="subtitle2" color="textSecondary">
                  Dateipfad:
                </Typography>
                <Typography variant="body2">{selectedVorlage.dateiPfad}</Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2" color="textSecondary" mb={1}>
                  Inhalt:
                </Typography>
                <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50', maxHeight: '400px', overflow: 'auto' }}>
                  <Typography
                    variant="body2"
                    component="pre"
                    sx={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace' }}
                  >
                    {selectedVorlage.content || 'Kein Inhalt verfügbar'}
                  </Typography>
                </Paper>
              </Box>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setViewDialogOpen(false)}>Schließen</Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Dialog zum Erstellen einer neuen Vorlage */}
      <Dialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Neue Vorlage erstellen</DialogTitle>
        <DialogContent dividers>
          <Box display="flex" flexDirection="column" gap={2}>
            <TextField
              label="Name"
              value={newVorlage.vorlageName}
              onChange={(e) =>
                setNewVorlage({ ...newVorlage, vorlageName: e.target.value })
              }
              fullWidth
              required
            />

            <FormControl fullWidth required>
              <InputLabel>Typ</InputLabel>
              <Select
                value={newVorlage.vorlageTyp}
                onChange={(e) =>
                  setNewVorlage({ ...newVorlage, vorlageTyp: e.target.value })
                }
                label="Typ"
              >
                <MenuItem value="Brief">Brief</MenuItem>
                <MenuItem value="Email">Email</MenuItem>
              </Select>
            </FormControl>

            <Box>
              <Button
                variant="outlined"
                component="label"
                fullWidth
                sx={{ mb: 1 }}
              >
                {selectedFile ? selectedFile.name : 'Datei hochladen (optional)'}
                <input
                  type="file"
                  hidden
                  accept=".md,.txt,.html,.docx"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) {
                      setSelectedFile(file);
                      // Setze Dateiformat basierend auf hochgeladener Datei
                      const ext = file.name.substring(file.name.lastIndexOf('.'));
                      setNewVorlage({ ...newVorlage, dateiFormat: ext });
                    }
                  }}
                />
              </Button>
              {selectedFile && (
                <Button
                  size="small"
                  onClick={() => setSelectedFile(null)}
                  color="error"
                >
                  Datei entfernen
                </Button>
              )}
            </Box>

            <TextField
              label="Dateipfad (nur wenn keine Datei hochgeladen)"
              value={newVorlage.dateiPfad}
              onChange={(e) =>
                setNewVorlage({ ...newVorlage, dateiPfad: e.target.value })
              }
              fullWidth
              disabled={!!selectedFile}
            />

            <TextField
              label="Beschreibung (optional)"
              value={newVorlage.beschreibung}
              onChange={(e) =>
                setNewVorlage({ ...newVorlage, beschreibung: e.target.value })
              }
              fullWidth
              multiline
              rows={3}
            />

            <TextField
              label="Kategorie (optional)"
              value={newVorlage.kategorie}
              onChange={(e) =>
                setNewVorlage({ ...newVorlage, kategorie: e.target.value })
              }
              fullWidth
            />

            {mandanten.length > 0 && (
              <FormControl fullWidth>
                <InputLabel>Mandant (optional)</InputLabel>
                <Select
                  value={newVorlage.mandantID || ''}
                  onChange={(e) =>
                    setNewVorlage({
                      ...newVorlage,
                      mandantID: e.target.value ? Number(e.target.value) : undefined,
                    })
                  }
                  label="Mandant (optional)"
                >
                  <MenuItem value="">
                    <em>Kein Mandant</em>
                  </MenuItem>
                  {mandanten.map((mandant) => (
                    <MenuItem key={mandant.mandantID} value={mandant.mandantID}>
                      {mandant.mandantName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            {gruppen.length > 0 && (
              <FormControl fullWidth>
                <InputLabel>Gruppe (optional)</InputLabel>
                <Select
                  value={newVorlage.gruppeID || ''}
                  onChange={(e) =>
                    setNewVorlage({
                      ...newVorlage,
                      gruppeID: e.target.value ? Number(e.target.value) : undefined,
                    })
                  }
                  label="Gruppe (optional)"
                >
                  <MenuItem value="">
                    <em>Keine Gruppe</em>
                  </MenuItem>
                  {gruppen.map((gruppe) => (
                    <MenuItem key={gruppe.gruppeID} value={gruppe.gruppeID}>
                      {gruppe.gruppenName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>Abbrechen</Button>
          <Button
            onClick={handleCreateVorlage}
            variant="contained"
            disabled={saving}
            startIcon={<SaveIcon />}
          >
            {saving ? 'Erstellen...' : 'Erstellen'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog zum Bearbeiten einer Vorlage */}
      <Dialog
        open={editDialogOpen}
        onClose={() => {
          if (contentChanged) {
            if (window.confirm('Es gibt ungespeicherte Änderungen. Möchten Sie wirklich schließen?')) {
              setEditDialogOpen(false);
              setIsDocxVorlage(false);
              setEditorLoading(false);
            }
          } else {
            setEditDialogOpen(false);
            setIsDocxVorlage(false);
            setEditorLoading(false);
          }
        }}
        maxWidth={isDocxVorlage ? 'xl' : 'sm'}
        fullWidth
        PaperProps={{
          sx: isDocxVorlage ? { height: '90vh' } : {}
        }}
      >
        <DialogTitle>
          Vorlage bearbeiten
          {isDocxVorlage && contentChanged && (
            <Chip
              label="Ungespeicherte Änderungen"
              color="warning"
              size="small"
              sx={{ ml: 2 }}
            />
          )}
        </DialogTitle>
        <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', overflow: isDocxVorlage ? 'hidden' : 'auto' }}>
          <Box display="flex" flexDirection="column" gap={2} sx={{ flex: isDocxVorlage ? 1 : 'none', overflow: isDocxVorlage ? 'hidden' : 'visible' }}>
            {/* Metadaten-Felder - kompakter für DOCX */}
            <Box display="flex" gap={2} flexWrap="wrap">
              <TextField
                label="Name"
                value={editVorlage.vorlageName}
                onChange={(e) =>
                  setEditVorlage({ ...editVorlage, vorlageName: e.target.value })
                }
                required
                size={isDocxVorlage ? 'small' : 'medium'}
                sx={{ flex: isDocxVorlage ? '1 1 200px' : '1 1 100%' }}
              />

              <FormControl required size={isDocxVorlage ? 'small' : 'medium'} sx={{ flex: isDocxVorlage ? '0 1 150px' : '1 1 100%' }}>
                <InputLabel>Typ</InputLabel>
                <Select
                  value={editVorlage.vorlageTyp}
                  onChange={(e) =>
                    setEditVorlage({ ...editVorlage, vorlageTyp: e.target.value })
                  }
                  label="Typ"
                >
                  <MenuItem value="Brief">Brief</MenuItem>
                  <MenuItem value="Email">Email</MenuItem>
                </Select>
              </FormControl>

              {!isDocxVorlage && (
                <TextField
                  label="Dateipfad (optional)"
                  value={editVorlage.dateiPfad}
                  onChange={(e) =>
                    setEditVorlage({ ...editVorlage, dateiPfad: e.target.value })
                  }
                  fullWidth
                />
              )}

              <TextField
                label="Beschreibung"
                value={editVorlage.beschreibung}
                onChange={(e) =>
                  setEditVorlage({ ...editVorlage, beschreibung: e.target.value })
                }
                size={isDocxVorlage ? 'small' : 'medium'}
                sx={{ flex: isDocxVorlage ? '1 1 300px' : '1 1 100%' }}
                multiline={!isDocxVorlage}
                rows={isDocxVorlage ? 1 : 3}
              />

              {mandanten.length > 0 && (
                <FormControl size={isDocxVorlage ? 'small' : 'medium'} sx={{ flex: isDocxVorlage ? '0 1 180px' : '1 1 100%' }}>
                  <InputLabel>Mandant</InputLabel>
                  <Select
                    value={editVorlage.mandantID || ''}
                    onChange={(e) =>
                      setEditVorlage({
                        ...editVorlage,
                        mandantID: e.target.value ? Number(e.target.value) : undefined,
                      })
                    }
                    label="Mandant"
                  >
                    <MenuItem value="">
                      <em>Kein Mandant</em>
                    </MenuItem>
                    {mandanten.map((mandant) => (
                      <MenuItem key={mandant.mandantID} value={mandant.mandantID}>
                        {mandant.mandantName}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}

              {gruppen.length > 0 && (
                <FormControl size={isDocxVorlage ? 'small' : 'medium'} sx={{ flex: isDocxVorlage ? '0 1 180px' : '1 1 100%' }}>
                  <InputLabel>Gruppe</InputLabel>
                  <Select
                    value={editVorlage.gruppeID || ''}
                    onChange={(e) =>
                      setEditVorlage({
                        ...editVorlage,
                        gruppeID: e.target.value ? Number(e.target.value) : undefined,
                      })
                    }
                    label="Gruppe"
                  >
                    <MenuItem value="">
                      <em>Keine Gruppe</em>
                    </MenuItem>
                    {gruppen.map((gruppe) => (
                      <MenuItem key={gruppe.gruppeID} value={gruppe.gruppeID}>
                        {gruppe.gruppenName}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            </Box>

            {/* Editor-Bereich */}
            {isDocxVorlage ? (
              <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
                <Typography variant="subtitle2" color="textSecondary" mb={1}>
                  Vorlagen-Inhalt bearbeiten:
                </Typography>
                <Box sx={{ flex: 1, border: '1px solid #ddd', borderRadius: 1, overflow: 'hidden' }}>
                  <SyncfusionEditor
                    ref={editorRef}
                    vorlageId={editVorlage.vorlageID}
                    height="100%"
                    onContentChange={() => setContentChanged(true)}
                  />
                </Box>
              </Box>
            ) : (
              <Box>
                <Typography variant="subtitle2" color="textSecondary" mb={1}>
                  Aktueller Dateiinhalt:
                </Typography>
                <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50', maxHeight: '300px', overflow: 'auto' }}>
                  <Typography
                    variant="body2"
                    component="pre"
                    sx={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace', fontSize: '0.875rem' }}
                  >
                    {editContent || 'Wird geladen...'}
                  </Typography>
                </Paper>
                <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
                  Hinweis: Der Dateiinhalt kann nur durch Hochladen einer neuen Datei geändert werden.
                </Typography>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            if (contentChanged) {
              if (window.confirm('Es gibt ungespeicherte Änderungen. Möchten Sie wirklich schließen?')) {
                setEditDialogOpen(false);
                setIsDocxVorlage(false);
                setEditorLoading(false);
              }
            } else {
              setEditDialogOpen(false);
              setIsDocxVorlage(false);
              setEditorLoading(false);
            }
          }}>
            Abbrechen
          </Button>
          {isDocxVorlage && (
            <Button
              onClick={handleSaveVorlageContent}
              variant="outlined"
              disabled={saving || !contentChanged}
              color="primary"
            >
              {saving ? 'Speichern...' : 'Inhalt speichern'}
            </Button>
          )}
          <Button
            onClick={handleUpdateVorlage}
            variant="contained"
            disabled={saving}
            startIcon={<SaveIcon />}
          >
            {saving ? 'Speichern...' : 'Metadaten speichern'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Vorlagen;
